import * as React from "react";

import { JobPipelineTypeSchema } from "@domain/schemas/BI";
import { useJobSetupStore } from "@presentation/stores/JobSetupStore";
import { useShallow } from "zustand/react/shallow";
import type { CreateJobDraft } from "@presentation/stores/JobSetupStore";

/**
 * The fields the job cannot be created without. Which of them actually apply
 * depends on the pipeline, so requiredness is a function of the state rather
 * than a fixed list.
 */
export type RequiredFieldKey = "name" | "softwareVersion" | "rawFiles" | "fastaFiles" | "libraryFiles";

export interface MissingRequirement {
  key: RequiredFieldKey;
  label: string;
}

/**
 * Amber, not red, for a required field that is still empty.
 *
 * A field the user has not reached yet is incomplete, not wrong. Red stays
 * reserved for genuine failures — a rejected submission, a failed API call — so
 * the two states are never mistaken for one another.
 */
export const INCOMPLETE_ACCENT = {
  background: "#fffbeb",
  border: "#f59e0b",
  text: "#92400e",
};

/**
The resting outline: a required field looks like any other until it is asked for.
*/
export const NEUTRAL_BORDER = "#ccc";

const FIELD_LABELS: Record<RequiredFieldKey, string> = {
  name: "Project name",
  softwareVersion: "Software version",
  rawFiles: "Raw files",
  fastaFiles: "FASTA files",
  libraryFiles: "Library files",
};

const REQUIRED_FIELD_ORDER: RequiredFieldKey[] = ["name", "softwareVersion", "rawFiles", "fastaFiles", "libraryFiles"];

const isBlank = (value?: string | null) => (value ?? "").trim() === "";

/**
 * Whether a field applies at all to the currently selected pipeline.
 *
 * Library-based analysis searches an existing library, so it needs no FASTA;
 * every other pipeline does. Library generation can start from the archives of a
 * previous run, in which case raw files are not needed a second time.
 */
export const isFieldRequired = (state: CreateJobDraft, key: RequiredFieldKey): boolean => {
  const isLibraryBased = state.pipelineType === JobPipelineTypeSchema.LibraryBased;
  const isLibraryGeneration = state.pipelineType === JobPipelineTypeSchema.LibraryGeneration;
  const hasArchiveFiles = (state.archiveFiles?.length ?? 0) > 0;

  switch (key) {
    case "name":
    case "softwareVersion": {
      return true;
    }
    case "rawFiles": {
      return !(isLibraryGeneration && hasArchiveFiles);
    }
    case "fastaFiles": {
      return !isLibraryBased;
    }
    case "libraryFiles": {
      return isLibraryBased;
    }
    default: {
      return false;
    }
  }
};

const isFieldFilled = (state: CreateJobDraft, key: RequiredFieldKey): boolean => {
  switch (key) {
    case "name": {
      return !isBlank(state.name);
    }
    case "softwareVersion": {
      return !isBlank(state.softwareVersion);
    }
    case "rawFiles": {
      return (state.rawFiles?.length ?? 0) > 0;
    }
    case "fastaFiles": {
      return (state.fastaFiles?.length ?? 0) > 0;
    }
    case "libraryFiles": {
      return (state.libraryFiles?.length ?? 0) > 0;
    }
    default: {
      return true;
    }
  }
};

/**
 * The single source of truth for "what is still missing", in form order.
 *
 * The submit gate, the summary next to the Create Job button and the per-field
 * highlighting all read this, so they cannot drift apart — previously each block
 * decided for itself and two of them ignored the pipeline entirely.
 */
export const getMissingRequirements = (state: CreateJobDraft): MissingRequirement[] =>
  REQUIRED_FIELD_ORDER.filter((key) => isFieldRequired(state, key) && !isFieldFilled(state, key)).map((key) => ({ key, label: FIELD_LABELS[key] }));

export interface RequiredFieldState {
  /**
  Whether to mark the field as required at all, for the current pipeline.
  */
  required: boolean;
  /**
   * Whether to draw attention to it. Only ever true after a submit attempt, so
   * the form never accuses the user of an omission they have not had a chance to
   * make.
   */
  highlight: boolean;
}

/**
Per-field view of {@link getMissingRequirements}, for the individual form blocks.
*/
export const useRequiredFieldState = (key: RequiredFieldKey): RequiredFieldState => {
  const { CreateJobState, showValidation } = useJobSetupStore(
    useShallow((state) => ({
      CreateJobState: state.CreateJobState,
      showValidation: state.showValidation,
    })),
  );

  const isRequired = isFieldRequired(CreateJobState, key);
  const isFilled = isFieldFilled(CreateJobState, key);

  return React.useMemo(() => ({ required: isRequired, highlight: showValidation && isRequired && !isFilled }), [isRequired, isFilled, showValidation]);
};

/**
The outline for a selection box that may or may not still be waiting for input.
*/
export const requiredFieldBorder = ({ highlight }: RequiredFieldState) => (highlight ? `1px solid ${INCOMPLETE_ACCENT.border}` : `1px solid ${NEUTRAL_BORDER}`);
